(() => {
  const getRequestSections = (cartItems) => Array.from(new Set(
    cartItems.getSectionsToRender()
      .filter(item => Boolean(document.querySelector('#' + item.id)))
      .map(section => section.section)
  )).filter(section => section !== undefined).slice(0, 5);

  const getErrorMessage = (response) => {
    const error = response?.errors || response?.description || response?.message;
    if (typeof error === 'string' && error.trim()) return error;
    if (Array.isArray(error)) return error.join(', ');
    if (error && typeof error === 'object') return Object.values(error).flat().join(', ');
    return window.cartStrings.error;
  };

  const showLineError = (line, message) => {
    document.querySelectorAll('.cart-item__error').forEach(element => element.classList.add('hidden'));
    const lineError = document.getElementById(`Line-item-error-${line}`);
    if (lineError) {
      lineError.classList.remove('hidden');
      lineError.querySelector('.cart-item__error-text').textContent = message;
      return;
    }

    const cartError = document.getElementById('cart-errors');
    if (cartError) cartError.textContent = message;
  };

  const announceUpdate = () => {
    const liveRegion = document.getElementById('cart-live-region-text');
    if (!liveRegion) return;
    liveRegion.textContent = window.cartStrings.successfully_line_item_updated;
    liveRegion.setAttribute('aria-hidden', 'false');
    setTimeout(() => liveRegion.setAttribute('aria-hidden', 'true'), 1000);
  };

  const installCartItemsExtension = () => {
    const cartItemsClass = customElements.get('cart-items');
    if (!cartItemsClass || cartItemsClass.prototype.updateProperties) return;

    cartItemsClass.prototype.updateProperties = async function (key, quantity, properties, line) {
      this.enableLoading(line);

      const body = JSON.stringify({
        id: key,
        quantity,
        properties,
        sections: getRequestSections(this),
        sections_url: window.location.pathname
      });

      try {
        const request = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
        const response = await request.json();

        if (!request.ok || response.errors || response.status) {
          const error = new Error(getErrorMessage(response));
          error.customerMessage = error.message;
          throw error;
        }

        this.renderContentsAndDependencies(response, line);
        announceUpdate();
        return response;
      } catch (error) {
        console.error(error);
        const customerMessage = error.customerMessage || window.cartStrings.error;
        showLineError(line, customerMessage);
        const customerError = new Error(customerMessage);
        customerError.customerMessage = customerMessage;
        throw customerError;
      } finally {
        this.querySelectorAll('.loading-overlay').forEach(overlay => overlay.classList.add('hidden'));
        this.disableLoading();
      }
    };
  };

  if (customElements.get('cart-items')) {
    installCartItemsExtension();
  } else {
    customElements.whenDefined('cart-items').then(installCartItemsExtension);
  }

  if (customElements.get('cart-line-properties')) return;

  customElements.define('cart-line-properties', class CartLineProperties extends HTMLElement {
    constructor() {
      super();
      this.summary = this.querySelector('[data-cart-line-properties-summary]');
      this.editor = this.querySelector('[data-cart-line-properties-editor]');
      this.editButton = this.querySelector('[data-cart-line-properties-edit]');
      this.saveButton = this.querySelector('[data-cart-line-properties-save]');
      this.cancelButton = this.querySelector('[data-cart-line-properties-cancel]');
      this.errorElement = this.querySelector('[data-product-option-error]');
      this.fields = Array.from(this.querySelectorAll('[data-product-option-field]'));
      this.initialFieldState = this.fields.map(field => ({
        field,
        value: field.value,
        checked: field.checked,
        selected: field.tagName === 'SELECT'
          ? Array.from(field.options).map(option => option.selected)
          : null
      }));
      this.originalProperties = this.parseOriginalProperties();

      this.editButton?.addEventListener('click', () => this.open());
      this.cancelButton?.addEventListener('click', () => this.close(true));
      this.saveButton?.addEventListener('click', () => this.save());
    }

    parseOriginalProperties() {
      try {
        const properties = JSON.parse(this.dataset.properties || '{}');
        if (Array.isArray(properties)) {
          const hasValidEntries = properties.every(property => (
            Array.isArray(property)
            && property.length === 2
            && typeof property[0] === 'string'
          ));

          return hasValidEntries ? Object.fromEntries(properties) : null;
        }

        return properties && typeof properties === 'object' ? properties : null;
      } catch (error) {
        console.error('Unable to parse cart line properties.', error);
        return null;
      }
    }

    open() {
      if (!this.editor) return;
      this.summary.hidden = true;
      this.editor.hidden = false;
      this.editButton.setAttribute('aria-expanded', 'true');
      this.fields[0]?.focus();
    }

    close(returnFocus = false) {
      this.restoreInitialValues();
      this.clearError();
      this.editor.hidden = true;
      this.summary.hidden = false;
      this.editButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.editButton.focus();
    }

    restoreInitialValues() {
      this.initialFieldState.forEach(({ field, value, checked, selected }) => {
        field.value = value;
        if ('checked' in field) field.checked = checked;
        if (selected) {
          Array.from(field.options).forEach((option, index) => {
            option.selected = selected[index];
          });
        }
        field.removeAttribute('aria-invalid');
      });
    }

    buildProperties() {
      const properties = { ...this.originalProperties };
      this.fields.filter(field => !field.disabled).forEach(field => {
        const name = field.getAttribute('data-product-option-name');
        if (name) properties[name] = field.value;
      });
      return properties;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    setLoading(loading) {
      this.toggleAttribute('aria-busy', loading);
      this.saveButton.disabled = loading;
      this.cancelButton.disabled = loading;
      this.saveButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
    }

    async save() {
      if (this.hasAttribute('aria-busy')) return;
      this.clearError();

      if (!this.originalProperties) {
        this.showError(window.cartStrings.error);
        return;
      }

      if (!window.ProductOptions?.validateAndReport(this)) return;

      const cartItems = this.closest('cart-items');
      if (!cartItems?.updateProperties) {
        this.showError(window.cartStrings.error);
        return;
      }

      this.setLoading(true);
      try {
        await cartItems.updateProperties(
          this.dataset.lineKey,
          Number(this.dataset.lineQuantity),
          this.buildProperties(),
          this.dataset.lineIndex
        );
      } catch (error) {
        if (this.isConnected) this.showError(error.message || window.cartStrings.error);
      } finally {
        if (this.isConnected) this.setLoading(false);
      }
    }
  });
})();
